import { useReport } from '../../hooks/useReport';
import { useHashScroll } from '../../hooks/useHashScroll';
import { TIMEZONE, REPORT_COLORS, CAPACITY } from '../../constants';
import { LoadingSkeleton } from '../../components/atoms/LoadingSkeleton/LoadingSkeleton';
import { ErrorMessage } from '../../components/atoms/ErrorMessage/ErrorMessage';
import { ReportSection } from '../report/ReportSection';
import { StatTiles } from '../report/StatTiles';
import { BandBars } from '../report/BandBars';
import { EnergyMixChart } from '../report/EnergyMixChart';
import { EnergyFlow } from '../report/EnergyFlow';
import { SupplyBar } from '../report/SupplyBar';
import { PeakDayChart } from '../report/PeakDayChart';
import { RestorationCurve } from '../report/RestorationCurve';
import { StormTable } from '../report/StormTable';
import { Cite } from '../report/Cite';
import { SourcesList } from '../report/SourcesList';
import './ReportTab.css';

const num = (n: number) => n.toLocaleString('en-CA');

/** Percentages are always shown to one decimal so a column of them lines up. */
const pct = (n: number) => `${n.toFixed(1)}%`;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', {
    timeZone: TIMEZONE, year: 'numeric', month: 'long', day: 'numeric',
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-CA', {
    timeZone: TIMEZONE, month: 'long', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

/** "2026-01" → "January 2026". */
function monthName(month: string): string {
  const [year, m] = month.split('-');
  return `${new Date(Number(year), Number(m) - 1, 1)
    .toLocaleString('en-CA', { month: 'long' })} ${year}`;
}

export function ReportTab() {
  const { data, isLoading, error } = useReport();
  useHashScroll(Boolean(data));

  if (isLoading) return <section className="App-section"><LoadingSkeleton variant="text" /></section>;
  if (error) return <section className="App-section"><ErrorMessage message={error.message} /></section>;
  if (!data) return null;

  const {
    period, energy, monthly, peakLoad, bestRenewable, fossil,
    gridAlerts, outages, caveats,
  } = data;

  // A browser holding a cached response from an older deploy can be missing
  // newer fields. Degrade to hiding those sections rather than blanking the
  // whole report behind an error boundary.
  const peakDay = data.peakDay ?? [];
  const restorationCurve = data.restorationCurve ?? [];
  const headroom = data.headroom ?? null;

  const clearBand = outages.bands.find((b) => b.label === 'Nobody out');
  const tinyBand = outages.bands.find((b) => b.label === '1–5 customers');
  const seriousPct = outages.bands
    .filter((b) => ['51–500 customers', '501–2,000 customers', 'More than 2,000'].includes(b.label))
    .reduce((sum, b) => sum + b.pctOfTime, 0);

  const worstPressure = data.pressureBands[0];
  const calmestPressure = data.pressureBands[data.pressureBands.length - 1];
  const pressureMultiplier = calmestPressure && calmestPressure.pctStormLevel > 0
    ? (worstPressure.pctStormLevel / calmestPressure.pctStormLevel).toFixed(1)
    : null;

  const countySpread = outages.restorationByCounty.map((c) => c.medianHours);
  const restorationIsEven = countySpread.length > 1
    && Math.max(...countySpread) - Math.min(...countySpread) < 0.25;

  const onIslandMw = energy.avgLoadMw - energy.avgImportMw;
  // Stated as used + exported so every renewable figure on the page reconciles
  // with the flow diagram. The province's wind+solar gauges agree with this to
  // within a fraction of a megawatt.
  const renewableGenerated = energy.avgRenewableUsedMw + energy.avgRenewableExportedMw;
  const pctExported = (100 * energy.avgRenewableExportedMw) / renewableGenerated;
  const fossilToIsland = Math.max(0, onIslandMw - energy.avgRenewableUsedMw);
  const within2h = restorationCurve.find((p) => p.hours === 2)?.pctRestored;
  const within6h = restorationCurve.find((p) => p.hours === 6)?.pctRestored;
  const peakHour = peakLoad
    ? Number(new Date(peakLoad.at).toLocaleString('en-CA', { timeZone: TIMEZONE, hour: '2-digit', hour12: false }))
    : 0;

  return (
    <section className="App-section ReportTab">
      <header className="ReportTab-hero">
        <h2 className="ReportTab-title">The State of PEI's Power Grid</h2>
        <p className="ReportTab-subtitle">
          Every five minutes since {formatDate(period.start)} we have recorded how much
          power Prince Edward Island was using, where it came from, and who was without
          it. This is what {Math.floor(period.days)} days of that record shows.
        </p>
        <p className="ReportTab-meta">
          {num(period.polls)} readings · {period.coveragePct}% coverage · updated{' '}
          {formatDateTime(data.generatedAt)}
        </p>
      </header>

      <ReportSection
        id="imports"
        number={1}
        title="Most of PEI's electricity comes from off-island"
        lede={
          <>
            Prince Edward Island has no hydroelectric generation and only modest
            on-island capacity, so it draws most of its power across four submarine
            cables from New Brunswick<Cite id="me-interconnection" />. Averaged across
            our whole record, {pct(energy.pctLoadImported)} of the island's electricity
            arrived that way. On-island wind, solar and diesel together covered demand
            in only {energy.pctTimeSelfSufficient}% of readings, and the island was
            never fully self-supplied — even at its most independent moment it was
            still importing {energy.minImportMw} MW.
          </>
        }
      >
        <SupplyBar
          segments={[
            { label: 'Imported from New Brunswick', mw: energy.avgImportMw, color: REPORT_COLORS.imported },
            { label: 'On-island renewables', mw: energy.avgRenewableUsedMw, color: REPORT_COLORS.renewable },
            { label: 'On-island diesel', mw: fossilToIsland, color: REPORT_COLORS.fossil },
          ]}
        />
        <StatTiles
          stats={[
            { label: 'Average demand', value: num(energy.avgLoadMw), unit: 'MW' },
            { label: 'Average imported', value: num(energy.avgImportMw), unit: 'MW', note: `${pct(energy.pctLoadImported)} of demand` },
            { label: 'Time able to self-supply', value: `${energy.pctTimeSelfSufficient}`, unit: '%' },
            { label: 'Lowest import ever recorded', value: num(energy.minImportMw), unit: 'MW' },
          ]}
        />
        <p className="ReportTab-aside">
          Published estimates of PEI's import share vary with the year and the method
          used: the Canada Energy Regulator puts it near 69% for 2023<Cite id="cer-pei" />,
          while other summaries cite about 85%<Cite id="atlantica-pei" />. Our{' '}
          {pct(energy.pctLoadImported)} is a continuous measurement over this particular
          window and is not directly comparable to either.
        </p>
      </ReportSection>

      <ReportSection
        id="exports"
        number={2}
        title="PEI exports renewable power at the same time as it imports"
        lede={
          <>
            The island generates an average of {renewableGenerated.toFixed(1)} MW of wind
            and solar, but only {energy.avgRenewableUsedMw} MW of it is used here. The
            remaining {energy.avgRenewableExportedMw} MW — {pct(pctExported)} of
            everything PEI's turbines and panels produce — goes off-island. This is not a
            rounding artefact of averaging: in <strong>{pct(energy.pctImportingWhileExporting)}</strong> of individual readings
            the island was importing and exporting at the same moment. The explanation is
            contractual rather than physical — Engie owns 108 MW of PEI wind and sells most
            of its output to New Brunswick, and close to half of the island's wind is
            committed off-island<Cite id="pembina-wind" />.
          </>
        }
      >
        <EnergyFlow energy={energy} />
      </ReportSection>

      <ReportSection
        id="monthly-mix"
        number={3}
        title="Where our power came from, month by month"
        lede={
          <>
            The mix shifts with the seasons. Demand is highest in winter — peak load on
            PEI falls in the heart of the heating season, pushed up by the growth of
            electric heat and heat pumps<Cite id="pei-energy-strategy" /> — and that is
            also when the imported share is largest. Solar output peaks in summer, when the
            island draws least. Part of that summer trough is the solar itself: rooftop panels
            are net-metered<Cite id="me-net-metering" />, so they show up here as demand that
            never appears rather than as supply.
          </>
        }
      >
        <EnergyMixChart monthly={monthly} />
      </ReportSection>

      {peakLoad && peakDay.length > 0 && (
        <ReportSection
          id="peak-day"
          number={4}
          title="The day the grid worked hardest"
          lede={
            <>
              The highest demand in our record came at {formatDateTime(peakLoad.at)}, when
              the island drew {num(peakLoad.loadMw)} MW.
              {peakLoad.temperatureC !== null
                && ` The average temperature across our four weather stations was ${peakLoad.temperatureC} °C.`}{' '}
              The shape of that day is the point: wind generation fell steadily from the
              early hours, demand climbed into the evening, and the diesel units that
              normally sit idle were brought up to {num(peakLoad.fossilMw)} MW while{' '}
              {num(peakLoad.importMw)} MW flowed in from New Brunswick.
            </>
          }
        >
          <PeakDayChart hours={peakDay} peakHour={peakHour} />
          <StatTiles
            stats={[
              { label: 'Peak demand', value: num(peakLoad.loadMw), unit: 'MW', note: formatDateTime(peakLoad.at) },
              { label: 'Imported at that moment', value: num(peakLoad.importMw), unit: 'MW' },
              { label: 'Diesel generation', value: num(peakLoad.fossilMw), unit: 'MW' },
              { label: 'Wind generation', value: num(peakLoad.windMw), unit: 'MW' },
            ]}
          />
        </ReportSection>
      )}

      <ReportSection
        id="diesel"
        number={5}
        title="Diesel generation is close to dormant"
        lede={
          <>
            PEI's on-island thermal plants are diesel combustion turbines kept for peaking
            and emergency backup rather than everyday supply. They ran during just{' '}
            {pct(fossil.pctTimeRunning)} of all readings, and for most of the year were
            effectively switched off
            {fossil.busiestMonth
              && `: in ${monthName(fossil.busiestMonth)} alone they were running ${pct(fossil.busiestMonthPctTime)} of the time`}.
            The most they ever produced at once was {num(fossil.peakMw)} MW.
          </>
        }
      >
        <StatTiles
          stats={[
            { label: 'Time running', value: `${fossil.pctTimeRunning}`, unit: '%' },
            { label: 'Highest output', value: num(fossil.peakMw), unit: 'MW' },
            ...(fossil.busiestMonth
              ? [{
                  label: 'Busiest month',
                  value: monthName(fossil.busiestMonth),
                  note: `running ${pct(fossil.busiestMonthPctTime)} of the time`,
                }]
              : []),
          ]}
        />
      </ReportSection>

      {bestRenewable && (
        <ReportSection
          id="greenest-moment"
          number={6}
          title="The greenest moment we recorded"
          lede={
            <>
              On {formatDateTime(bestRenewable.at)}, on-island renewables were meeting{' '}
              {bestRenewable.pctOfLoad}% of everything Prince Edward Island was using —{' '}
              {num(bestRenewable.windMw)} MW of wind and {num(bestRenewable.solarMw)} MW of
              solar against a demand of {num(bestRenewable.loadMw)} MW. Mild spring weather
              keeps demand low exactly when the wind still blows hard.
            </>
          }
        >
          <StatTiles
            stats={[
              { label: 'Renewable share of demand', value: `${bestRenewable.pctOfLoad}`, unit: '%' },
              { label: 'Wind at that moment', value: num(bestRenewable.windMw), unit: 'MW' },
              { label: 'Demand', value: num(bestRenewable.loadMw), unit: 'MW' },
            ]}
          />
        </ReportSection>
      )}

      <ReportSection
        id="outage-frequency"
        number={7}
        title="How often is anyone actually without power?"
        lede={
          <>
            {clearBand && `For ${pct(clearBand.pctOfTime)} of the time, nobody on the island was out.`}
            {tinyBand && ` A further ${pct(tinyBand.pctOfTime)} of the time, between one and
              five customers were affected — small, localised interruptions rather than
              anything island-wide.`}{' '}
            Larger interruptions, affecting more than fifty customers, accounted for{' '}
            {pct(seriousPct)} of the record. Across the whole period the island lost{' '}
            {num(outages.totalCustomerHoursLost)} customer-hours of power.
          </>
        }
      >
        <BandBars
          caption="Share of all readings by the number of customers without power"
          bands={outages.bands.map((b) => ({
            label: b.label,
            value: b.pctOfTime,
            display: pct(b.pctOfTime),
            context: `${num(b.readings)} readings`,
          }))}
        />
      </ReportSection>

      <ReportSection
        id="wind"
        number={8}
        title="High wind matters — but only past a point"
        lede={
          <>
            Wind speed on its own is a poor predictor of outages: across the record there is
            almost no straight-line relationship between how hard it is blowing and how many
            people are out. What matters is crossing a threshold. Below 70 km/h, gusts make
            little difference; above it, outages climb sharply.
          </>
        }
      >
        <BandBars
          caption="Average customers without power, by the strongest gust recorded anywhere on the island"
          bands={data.gustBands.map((b) => ({
            label: b.label,
            value: b.avgCustomersOut,
            display: num(b.avgCustomersOut),
            context: `${pct(b.pctStormLevel)} storm-level`,
          }))}
        />
      </ReportSection>

      <ReportSection
        id="pressure"
        number={9}
        title="The barometer is the better warning sign"
        lede={
          <>
            Falling air pressure marks an approaching storm system, and it tracks outage risk
            far more reliably than wind speed does.
            {pressureMultiplier && ` When pressure dropped below 98 kPa, the chance of a large
              outage was about ${pressureMultiplier} times higher than in settled,
              high-pressure conditions.`}{' '}
            If you want one number to watch before a storm, watch this one.
          </>
        }
      >
        <BandBars
          caption="Share of readings with more than 500 customers out, by lowest recorded air pressure"
          bands={data.pressureBands.map((b) => ({
            label: b.label,
            value: b.pctStormLevel,
            display: pct(b.pctStormLevel),
            context: `avg ${num(b.avgCustomersOut)} out`,
          }))}
        />
      </ReportSection>

      <ReportSection
        id="restoration"
        number={10}
        title="How quickly does the power come back?"
        lede={
          <>
            Half of all community outages were resolved within {outages.medianRestorationHours} hours,
            {within2h && ` ${within2h}% within two hours`}
            {within6h && ` and ${within6h}% within six`}.
            {restorationIsEven
              ? ' That median is effectively identical in all three counties — where you live on the island did not change how quickly power was typically restored.'
              : ' Median restoration times differed between counties.'}
          </>
        }
      >
        {restorationCurve.length > 0 && (
          <RestorationCurve points={restorationCurve} medianHours={outages.medianRestorationHours} />
        )}
        <div className="ReportTab-countyGrid">
          {outages.restorationByCounty.map((c) => (
            <div className="ReportTab-county" key={c.county}>
              <div className="ReportTab-countyName">{c.county} County</div>
              <div className="ReportTab-countyStat">
                <span>{c.medianHours} h</span> median
              </div>
              <div className="ReportTab-countyNote">
                {c.p90Hours} h for the slowest 10% · {num(c.incidents)} incidents
              </div>
            </div>
          ))}
        </div>
        {caveats && caveats.longOutagesOver24h > 0 && (
          <p className="ReportTab-aside">
            The very longest interruptions are not community-wide events. Of the{' '}
            {caveats.longOutagesOver24h} that ran beyond 24 hours,{' '}
            {caveats.longOutagesOver24hAffectingTwoOrFewer} affected two customers or fewer.
          </p>
        )}
      </ReportSection>

      <ReportSection
        id="biggest-outages"
        number={11}
        title="The biggest outages — and the conditions around them"
        lede={
          <>
            We recorded {outages.stormCount} separate storm-scale events, together accounting
            for {pct(outages.pctTimeInStormState)} of the period. The largest affected{' '}
            {num(outages.peakCustomersOut)} customers at once. Several of the biggest happened
            in calm conditions, which means high wind cannot explain them. The outage feed does
            not publish a cause<Cite id="me-outage-map" />, so what actually took those lines
            down is not something this data can tell us.
          </>
        }
      >
        <StormTable storms={data.topStorms} />
      </ReportSection>

      {headroom && (
        <ReportSection
          id="headroom"
          number={12}
          title="How close the island came to running short"
          lede={
            <>
              Meeting demand and having supply to spare are different things. Wind covers a
              large share of the island's load, but it cannot be dispatched, so the question
              that matters is what the grid could have carried without it. We measure that
              against {num(headroom.firmCapacityMw)} MW: {num(CAPACITY.nbImportMax)} MW of
              interconnection plus {num(CAPACITY.fossilFuelMax)} MW of dispatchable fossil
              generation. That first figure is a limit set by New Brunswick-side transmission
              rather than by the cables, which are rated far higher<Cite id="me-capacity-filing" />,
              and our own inferred flows run above it in{' '}
              {pct(caveats.pctImportOverInterconnectLimit)} of readings. Counting only up to it
              is deliberate: supply beyond a firm limit is not supply the island can rely on.
              {/* formatDateTime ends in "a.m."/"p.m.", so it closes the sentence itself. */}
              The thinnest margin in our record was {num(headroom.minHeadroomMw)} MW, on{' '}
              {formatDateTime(headroom.minHeadroomAt)}
              {headroom.pctTimeNonFirmDependent > 0
                ? ` For ${pct(headroom.pctTimeNonFirmDependent)} of readings, demand ran above
                   that figure outright: had the wind stopped in those moments, firm supply
                   alone could not have met the load, by as much as
                   ${num(headroom.maxNonFirmDependenceMw)} MW.`
                : ' Demand stayed below that figure in every reading we hold.'}
            </>
          }
        >
          <BandBars
            bands={headroom.bands.map((b) => ({
              label: b.label,
              value: b.pctOfTime,
              display: pct(b.pctOfTime),
              context: `${num(b.readings)} readings`,
            }))}
            caption="Share of readings by spare firm supply"
          />

          {headroom.tightest.length > 0 && (
            <StatTiles
              stats={headroom.tightest.slice(0, 3).map((t) => ({
                label: formatDateTime(t.at),
                value: `${num(t.headroomMw)} MW`,
                note: `${num(t.loadMw)} MW demand · ${num(t.windMw)} MW wind`
                  + `${t.temperatureC === null ? '' : ` · ${t.temperatureC} °C`}`,
              }))}
            />
          )}

          {headroom.alertComparison && (
            <p className="ReportTab-aside">
              These are our numbers, not the utility's judgement — so it is worth checking
              them against it. Across the{' '}
              {num(headroom.alertComparison.readingsDuringAlert)} readings that fall inside a
              period Maritime Electric publicly flagged, the tightest margin was{' '}
              {num(headroom.alertComparison.minHeadroomDuringAlertMw)} MW, against{' '}
              {num(headroom.alertComparison.minHeadroomOutsideAlertMw)} MW at the tightest
              moment outside one.
            </p>
          )}
        </ReportSection>
      )}

      {gridAlerts.length > 0 && (
        <ReportSection
          id="grid-alerts"
          number={13}
          title="When the utility warned the grid was tight"
          lede={
            <>
              Maritime Electric publishes a grid status alongside its outage map. We have only
              been recording it since {formatDate(caveats.gridStatusStart)} — {caveats.gridStatusDays} of
              the {Math.floor(period.days)} days in this report — and within that window it left
              the normal state {gridAlerts.length} times. Some of these fall on consecutive days
              and belong to the same cold snap. In the period we observed, load shedding was
              never once recorded: the grid was warned about, but never actually rationed. That
              is a statement about Maritime Electric's system alone — Summerside runs its own
              utility and publishes no comparable status<Cite id="cbc-two-utilities" />, so this
              report cannot say what its grid was doing on the same nights.
            </>
          }
        >
          <div className="ReportTab-alerts">
            {gridAlerts.map((a) => (
              <div className="ReportTab-alert" key={a.start}>
                <span className="ReportTab-alertStatus">{a.status}</span>
                <span>{formatDateTime(a.start)}</span>
                <span className="ReportTab-alertHours">{a.hours} hours</span>
              </div>
            ))}
          </div>
        </ReportSection>
      )}

      <ReportSection
        id="method"
        number={14}
        title="How this was measured, and what it cannot tell you"
        lede="Everything above is measured from public data this site records continuously. Being clear about the limits matters as much as the findings."
      >
        <div className="ReportTab-method">
          <p>
            <strong>Coverage.</strong> Outages and generation are sampled every five minutes
            and weather every fifteen, giving {num(period.polls)} readings and{' '}
            {period.coveragePct}% of a perfect record. The longest single gap was{' '}
            {period.longestGapMinutes} minutes. Not every source starts on the same day:
            outages and generation from {formatDate(caveats.outagesStart)}, weather from{' '}
            {formatDate(caveats.weatherStart)}
            {caveats.gridStatusStart
              ? `, and grid status only from ${formatDate(caveats.gridStatusStart)}`
              : ', and grid status has not been recorded for this period'}.
          </p>
          <p>
            <strong>This is Maritime Electric's grid, not all of PEI's.</strong> Summerside runs
            its own municipal utility, serving about 7,000 customers, and publishes no equivalent
            outage feed or grid status<Cite id="cbc-two-utilities" />. Every outage, restoration
            and community figure here therefore covers Maritime Electric's territory only. The
            energy figures work the other way: the province's gauges are island-wide and do
            include Summerside's generation. The boundary is not clean either — some homes inside
            Summerside sit on Maritime Electric's system — so this report does not attempt to
            separate the two.
          </p>
          <p>
            <strong>Imports are inferred, not measured.</strong> We do not read the cables
            directly. Imported power is what remains once on-island renewable and diesel
            generation are subtracted from total demand, using the Government of PEI's
            published figures<Cite id="gov-pei-renewable" />. That residual is an
            approximation, and we can show its limits: the interconnection is rated to
            300 MW<Cite id="me-capacity-filing" />, yet our inferred figure exceeded that in{' '}
            {caveats.pctImportOverInterconnectLimit}% of readings, peaking at{' '}
            {caveats.maxImportMw} MW. Treat the import numbers as close, not exact.
          </p>
          <p>
            <strong>The solar figure is net in two senses.</strong> The province publishes solar
            and battery output as a single gauge, which goes negative while the battery charges —
            it does so in {pct(caveats.pctSolarReadingsNegative)} of readings. The gauge also
            covers only the utility-scale farms and the Summerside battery: customer-owned solar
            is net-metered, so it offsets its owner's consumption behind the meter and never
            appears as supply at all<Cite id="me-net-metering" />. Solar averages here are net of
            battery charging and exclude rooftop generation entirely, so they understate what the
            island's panels actually produced.
          </p>
          <p>
            <strong>Load is net load.</strong> Because rooftop solar is subtracted from demand
            rather than added to supply, every load figure in this report is consumption minus
            whatever those panels were producing at the time. The effect is largest on sunny
            summer middays and absent after dark, so the winter and evening figures here are the
            least affected. It also means the renewable share of supply is understated, since
            rooftop generation is missing from both sides of that ratio.
          </p>
          {headroom && (
            <p>
              <strong>Headroom is measured against nameplate capacity.</strong> The{' '}
              {num(headroom.firmCapacityMw)} MW figure assumes the full{' '}
              {num(CAPACITY.nbImportMax)} MW interconnection and every fossil unit available at
              once, which is an optimistic reading — units go out for maintenance, and the
              interconnection limit is set by New Brunswick-side transmission rather than the
              cables<Cite id="me-capacity-filing" />. The highest fossil output we have actually
              observed is {num(headroom.observedFossilPeakMw)} MW, so a floor built from measured
              output rather than nameplate puts firm supply at{' '}
              {num(headroom.observedFirmCapacityMw)} MW and demand above it in{' '}
              {pct(headroom.pctTimeNonFirmDependentObserved)} of readings rather than{' '}
              {pct(headroom.pctTimeNonFirmDependent)}. Treat the pair as a range. Because load is
              net of rooftop solar, the margin is also flattered on sunny days — though not at the
              winter evening peaks where it is thinnest.
            </p>
          )}
          <p>
            <strong>Weather is island-wide.</strong> Readings come from four stations —
            Charlottetown, Summerside, Souris and Tignish<Cite id="eccc" /> — and the figures
            here use the most extreme value across them, so conditions at any individual
            outage may have differed. {pct(caveats.pctReadingsWithoutWeather)} of outage
            readings have no matching weather observation and are excluded from the wind and
            pressure sections.
          </p>
          <p>
            <strong>We cannot rank communities fairly.</strong> Maritime Electric publishes how
            many customers are out, but not how many it serves in each community. Without that
            denominator, a reliability rate per community cannot honestly be calculated, so
            this report does not attempt one.
          </p>
          <p>
            <strong>Causes are not published.</strong> The outage feed does not say why the
            lights went out. Where this report notes that an event happened in calm conditions,
            that is a statement about the weather at the time and not a claim about the cause.
          </p>
        </div>

        <h4 className="ReportTab-sourcesHeading">Sources</h4>
        <SourcesList />
      </ReportSection>
    </section>
  );
}
